import { assertGradient } from "./gradient";
import { blendModes } from "./effects";
import { isInteractive } from "./interactive";
import { lineStyles, type LineStyle } from "./dashes";
import type { SceneNode } from "../scene/model";
import { vectorPath } from "./vector-path";
import { isNativeShape } from "./shapes";

export const colorPattern = /^#[0-9a-f]{6}$/i;
export function shapeStyle(node: SceneNode) {
  const a = node.appearance;
  return {
    fill: String(a.fill),
    fillEnabled: a.fillEnabled !== false,
    stroke: String(a.stroke ?? "#28334a"),
    strokeEnabled: a.strokeEnabled === true,
    strokeWidth: a.strokeWidth ?? 2,
    opacity: a.opacity ?? 1,
    strokeDash: a.strokeDash as number[] | undefined,
    lineCap: (a.lineCap ?? "round") as CanvasLineCap,
    lineStyle: (a.lineStyle ?? "solid") as LineStyle,
  };
}
export function strokeRadius(node: SceneNode) {
  const style = shapeStyle(node);
  return (isNativeShape(node) || node.kind === "path") && style.strokeEnabled
    ? style.strokeWidth / 2
    : 0;
}
/** Legacy flags still render saved documents; controls express visibility as width/alpha. */
export function appearanceValue(node: SceneNode, field: AppearanceField) {
  const style = shapeStyle(node);
  return field === "strokeWidth" && !style.strokeEnabled ? 0 : style[field];
}
export function fillAlpha(node: SceneNode) {
  return node.appearance.fillEnabled === false
    ? 0
    : Number(node.appearance.fillAlpha ?? 1);
}
export function hasPaint(node: SceneNode) {
  return (
    node.appearance.opacity !== 0 &&
    (node.kind === "image" ||
      isInteractive(node) ||
      shapeStyle(node).fillEnabled ||
      strokeRadius(node) > 0)
  );
}
export function assertNativeAppearance(node: SceneNode) {
  assertGradient(node.appearance.fillGradient);
  assertGradient(node.appearance.strokeGradient);
  const a = node.appearance,
    shape = isNativeShape(node) || node.kind === "path";
  const allowed = [
    "fill",
    "opacity",
    "visible",
    "blendMode",
    "fillGradient",
    "strokeGradient",
    "fillAlpha",
    "strokeAlpha",
    ...(shape
      ? [
          "fillEnabled",
          "stroke",
          "strokeEnabled",
          "strokeWidth",
          "lineStyle",
          "strokeDash",
          "lineCap",
        ]
      : []),
  ];
  if (
    (a.strokeDash !== undefined &&
      (!Array.isArray(a.strokeDash) ||
        !a.strokeDash.length ||
        a.strokeDash.length > 32 ||
        a.strokeDash.some(
          (v) =>
            typeof v !== "number" || !Number.isFinite(v) || v < 0 || v > 10000,
        ) ||
        !a.strokeDash.some((v) => v > 0))) ||
    (a.lineCap !== undefined &&
      !["butt", "round", "square"].includes(String(a.lineCap))) ||
    (a.blendMode !== undefined &&
      !(blendModes as readonly string[]).includes(a.blendMode)) ||
    [a.fillAlpha, a.strokeAlpha].some(
      (v) =>
        v !== undefined &&
        (typeof v !== "number" || !Number.isFinite(v) || v < 0 || v > 1),
    ) ||
    Object.keys(a).some((k) => !allowed.includes(k)) ||
    typeof a.fill !== "string" ||
    !colorPattern.test(a.fill) ||
    typeof a.opacity !== "number" ||
    !Number.isFinite(a.opacity) ||
    a.opacity < 0 ||
    a.opacity > 1 ||
    (a.stroke !== undefined &&
      (typeof a.stroke !== "string" || !colorPattern.test(a.stroke))) ||
    (a.strokeWidth !== undefined &&
      (!Number.isFinite(a.strokeWidth) ||
        a.strokeWidth < 0 ||
        a.strokeWidth > 200)) ||
    (a.lineStyle === "hand" && !isNativeShape(node)) ||
    (a.lineStyle !== undefined &&
      !lineStyles.includes(a.lineStyle as LineStyle)) ||
    [a.fillEnabled, a.strokeEnabled, a.visible].some(
      (v) => v !== undefined && typeof v !== "boolean",
    )
  )
    throw new Error(
      "Этот проект использует неподдерживаемое оформление. Текущий документ сохранён на холсте.",
    );
}
export type AppearanceField =
  | "fill"
  | "fillEnabled"
  | "stroke"
  | "strokeEnabled"
  | "strokeWidth"
  | "opacity"
  | "lineStyle";
/** Open native strokes have no enclosed area; imported SVG retains its own fill semantics. */
export function supportsFill(node: SceneNode) {
  return (
    isNativeShape(node) ||
    (node.kind === "path" &&
      (!!node.geometry.svgPath || vectorPath(node).closed))
  );
}
export function supportsAppearance(node: SceneNode, field: AppearanceField) {
  return field === "fill" || field === "fillEnabled"
    ? supportsFill(node)
    : field === "opacity" || isNativeShape(node) || node.kind === "path";
}
export function changeAppearance(
  nodes: readonly SceneNode[],
  field: AppearanceField,
  value: string | number | boolean,
) {
  return nodes
    .filter(
      (n) =>
        supportsAppearance(n, field) &&
        (field !== "lineStyle" || value !== "hand" || isNativeShape(n)),
    )
    .map((node) => {
      const appearance = { ...node.appearance, [field]: value };
      if (field === "lineStyle") delete appearance.strokeDash;
      if (field === "fill") appearance.fillEnabled = true;
      if (field === "strokeWidth") appearance.strokeEnabled = Number(value) > 0;
      const changed = { ...node, appearance };
      assertNativeAppearance(changed);
      return changed;
    });
}
